import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface Upload {
  id: string;
  title: string;
  uploader_name: string;
  uploader_email: string;
  file_size: number;
  status: 'pending_review' | 'approved' | 'rejected';
  created_at: string;
}

export default function UploadModeration() {
  const { authToken } = useAuth();
  const [pendingUploads, setPendingUploads] = useState<Upload[]>([]);
  const [approvedUploads, setApprovedUploads] = useState<Upload[]>([]);
  const [rejectedUploads, setRejectedUploads] = useState<Upload[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [showApproved, setShowApproved] = useState(false);
  const [showRejected, setShowRejected] = useState(false);

  const headers: Record<string, string> = {
    Authorization: `Bearer ${authToken}`,
    'Content-Type': 'application/json',
  };

  const fetchUploads = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [pendingRes, approvedRes, rejectedRes] = await Promise.all([
        fetch(`${API_BASE}/admin/uploads?status=pending_review`, { headers }),
        fetch(`${API_BASE}/admin/uploads?status=approved`, { headers }),
        fetch(`${API_BASE}/admin/uploads?status=rejected`, { headers }),
      ]);

      if (!pendingRes.ok) throw new Error(`Failed to fetch uploads: ${pendingRes.status}`);

      const pendingData = await pendingRes.json();
      const approvedData = approvedRes.ok ? await approvedRes.json() : { uploads: [] };
      const rejectedData = rejectedRes.ok ? await rejectedRes.json() : { uploads: [] };

      setPendingUploads(pendingData.uploads || pendingData || []);
      setApprovedUploads(approvedData.uploads || approvedData || []);
      setRejectedUploads(rejectedData.uploads || rejectedData || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load uploads');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    fetchUploads();
  }, [fetchUploads]);

  const reviewUpload = async (upload: Upload, status: 'approved' | 'rejected') => {
    setReviewingId(upload.id);
    try {
      const res = await fetch(`${API_BASE}/admin/uploads/${upload.id}/review`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error(`Failed to review upload: ${res.status}`);

      // Move the upload to the correct list
      setPendingUploads((prev) => prev.filter((u) => u.id !== upload.id));
      const updatedUpload = { ...upload, status };
      if (status === 'approved') {
        setApprovedUploads((prev) => [updatedUpload, ...prev]);
      } else {
        setRejectedUploads((prev) => [updatedUpload, ...prev]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to review upload');
    } finally {
      setReviewingId(null);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-arena-accent border-t-transparent rounded-full animate-spin" />
        <span className="ml-3 text-arena-muted">Loading uploads...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg">
        {error}
        <button onClick={fetchUploads} className="ml-3 underline hover:no-underline">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Pending Review */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-arena-text">
            Pending Review
            {pendingUploads.length > 0 && (
              <span className="ml-2 inline-block px-2 py-0.5 text-xs rounded-full bg-yellow-500/20 text-yellow-400">
                {pendingUploads.length}
              </span>
            )}
          </h3>
          <button onClick={fetchUploads} className="btn btn-secondary text-sm">
            Refresh
          </button>
        </div>

        {pendingUploads.length === 0 ? (
          <div className="card p-8 text-center text-arena-muted">
            No uploads pending review. All clear.
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {pendingUploads.map((upload) => (
              <div key={upload.id} className="card p-4">
                <div className="mb-3">
                  <h4 className="font-medium text-arena-text truncate">{upload.title}</h4>
                  <div className="text-sm text-arena-muted mt-1">
                    {upload.uploader_name} ({upload.uploader_email})
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs text-arena-muted mb-4">
                  <span>{formatFileSize(upload.file_size)}</span>
                  <span>{formatDate(upload.created_at)}</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => reviewUpload(upload, 'approved')}
                    disabled={reviewingId === upload.id}
                    className={`btn text-sm flex-1 bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30 ${
                      reviewingId === upload.id ? 'opacity-50 cursor-wait' : ''
                    }`}
                  >
                    {reviewingId === upload.id ? '...' : 'Approve'}
                  </button>
                  <button
                    onClick={() => reviewUpload(upload, 'rejected')}
                    disabled={reviewingId === upload.id}
                    className={`btn text-sm flex-1 bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 ${
                      reviewingId === upload.id ? 'opacity-50 cursor-wait' : ''
                    }`}
                  >
                    {reviewingId === upload.id ? '...' : 'Reject'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Approved Uploads (collapsed) */}
      <div>
        <button
          onClick={() => setShowApproved(!showApproved)}
          className="flex items-center gap-2 text-sm font-medium text-arena-muted hover:text-arena-text transition-colors"
        >
          <span className={`transition-transform ${showApproved ? 'rotate-90' : ''}`}>
            &#9654;
          </span>
          Approved ({approvedUploads.length})
        </button>
        {showApproved && (
          <div className="mt-3 space-y-2">
            {approvedUploads.length === 0 ? (
              <p className="text-sm text-arena-muted pl-5">No approved uploads yet.</p>
            ) : (
              approvedUploads.map((upload) => (
                <div
                  key={upload.id}
                  className="flex items-center justify-between p-3 bg-arena-surface rounded-lg"
                >
                  <div>
                    <span className="text-sm font-medium text-arena-text">{upload.title}</span>
                    <span className="ml-2 text-xs text-arena-muted">
                      by {upload.uploader_name}
                    </span>
                  </div>
                  <span className="text-xs text-green-400">approved</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Rejected Uploads (collapsed) */}
      <div>
        <button
          onClick={() => setShowRejected(!showRejected)}
          className="flex items-center gap-2 text-sm font-medium text-arena-muted hover:text-arena-text transition-colors"
        >
          <span className={`transition-transform ${showRejected ? 'rotate-90' : ''}`}>
            &#9654;
          </span>
          Rejected ({rejectedUploads.length})
        </button>
        {showRejected && (
          <div className="mt-3 space-y-2">
            {rejectedUploads.length === 0 ? (
              <p className="text-sm text-arena-muted pl-5">No rejected uploads.</p>
            ) : (
              rejectedUploads.map((upload) => (
                <div
                  key={upload.id}
                  className="flex items-center justify-between p-3 bg-arena-surface rounded-lg"
                >
                  <div>
                    <span className="text-sm font-medium text-arena-text">{upload.title}</span>
                    <span className="ml-2 text-xs text-arena-muted">
                      by {upload.uploader_name}
                    </span>
                  </div>
                  <span className="text-xs text-red-400">rejected</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
